import { FloatingJoystick } from './FloatingJoystick.js'

const JOYSTICK_SIZE = 108
const PIXEL_GRID = 3
const DIRECT_TOUCH_HOLD_MS = 140

function clampMovementVector(vector) {
  const x = Number.isFinite(vector?.x) ? vector.x : 0
  const y = Number.isFinite(vector?.y) ? vector.y : 0
  const magnitude = Math.hypot(x, y)

  if (magnitude <= 1) return { x, y }
  return {
    x: x / magnitude,
    y: y / magnitude
  }
}

export function getGamepadMenuDirection(gamepad, deadzone = 0.55) {
  if (!gamepad) return 0
  if (gamepad.buttons?.[12]?.pressed) return -1
  if (gamepad.buttons?.[13]?.pressed) return 1
  const y = Number(gamepad.axes?.[1]) || 0
  if (y <= -deadzone) return -1
  if (y >= deadzone) return 1
  return 0
}

export function getGamepadMenuNavigation(gamepad, deadzone = 0.55) {
  if (!gamepad) return { x: 0, y: 0 }
  if (gamepad.buttons?.[12]?.pressed) return { x: 0, y: -1 }
  if (gamepad.buttons?.[13]?.pressed) return { x: 0, y: 1 }
  if (gamepad.buttons?.[14]?.pressed) return { x: -1, y: 0 }
  if (gamepad.buttons?.[15]?.pressed) return { x: 1, y: 0 }

  const leftX = Number(gamepad.axes?.[0]) || 0
  const leftY = Number(gamepad.axes?.[1]) || 0
  const rightX = Number(gamepad.axes?.[2]) || 0
  const rightY = Number(gamepad.axes?.[3]) || 0
  const x = Math.abs(rightX) > Math.abs(leftX) ? rightX : leftX
  const y = Math.abs(rightY) > Math.abs(leftY) ? rightY : leftY

  if (Math.max(Math.abs(x), Math.abs(y)) < deadzone) return { x: 0, y: 0 }
  return Math.abs(x) > Math.abs(y)
    ? { x: Math.sign(x), y: 0 }
    : { x: 0, y: Math.sign(y) }
}

export function getGamepadMovementAxes(gamepad) {
  // Character movement always uses this controller's physical right stick.
  const offset = 0
  return {
    x: Number(gamepad?.axes?.[offset]) || 0,
    y: Number(gamepad?.axes?.[offset + 1]) || 0
  }
}

export function getGamepadMovementVector(gamepad, deadzone = 0.15) {
  let { x, y } = getGamepadMovementAxes(gamepad)

  if (Math.abs(x) < deadzone) x = 0
  if (Math.abs(y) < deadzone) y = 0

  // Some otherwise usable controllers expose fewer than the standard 16
  // buttons. Missing D-pad buttons must not crash character movement.
  if (gamepad?.buttons?.[12]?.pressed) y = -1
  if (gamepad?.buttons?.[13]?.pressed) y = 1
  if (gamepad?.buttons?.[14]?.pressed) x = -1
  if (gamepad?.buttons?.[15]?.pressed) x = 1

  return { x, y }
}

export function getGamepadMapNavigation(gamepad, deadzone = 0.55) {
  if (!gamepad) return { x: 0, y: 0 }
  if (gamepad.buttons?.[12]?.pressed) return { x: 0, y: -1 }
  if (gamepad.buttons?.[13]?.pressed) return { x: 0, y: 1 }
  if (gamepad.buttons?.[14]?.pressed) return { x: -1, y: 0 }
  if (gamepad.buttons?.[15]?.pressed) return { x: 1, y: 0 }

  // The right stick changes maps. The physical left stick remains dedicated
  // to moving the white hand over the enlarged map.
  const x = Number(gamepad.axes?.[0]) || 0
  const y = Number(gamepad.axes?.[1]) || 0
  if (Math.max(Math.abs(x), Math.abs(y)) < deadzone) return { x: 0, y: 0 }
  return Math.abs(x) > Math.abs(y)
    ? { x: Math.sign(x), y: 0 }
    : { x: 0, y: Math.sign(y) }
}

export class InputManager {
  constructor(game) {
    this.game = game
    this.keys = {}
    this.keysJustPressed = {}
    this.mouseX = 0
    this.mouseY = 0
    this.mouseDown = { left: false }
    
    // Gamepad state
    this.gamepad = null
    this.gamepadIndex = null
    this.prevGamepadButtons = []
    this.prevGamepadMenuDirection = 0
    this.prevGamepadMenuNavigation = { x: 0, y: 0 }
    this.gamepadMenuRepeatAt = 0
    this.gamepadDeadzone = 0.15
    this.gamepadHand = document.getElementById('gamepad-hand')
    this.gamepadHandActive = false
    this.gamepadRunActive = false
    this.gamepadHandPosition = { x: window.innerWidth / 2, y: window.innerHeight / 2 }
    this.gamepadHandLastUpdate = performance.now()
    this.drawGamepadHand()

    // Touch state
    this.touchSurface = this.game.renderer.canvas
    this.touchJoystickElement = document.getElementById('touch-joystick')
    this.touchJoystickKnob = document.getElementById('touch-joystick-knob')
    this.primaryActionButton = document.getElementById('btn-bottom')
    this.touchArrowButton = document.getElementById('btn-top')
    this.touchShieldButton = document.getElementById('btn-left')
    this.touchBackButton = document.getElementById('btn-right')
    this.touchShieldHeld = false
    this.touchControlModeButton = document.getElementById('touch-control-mode-btn')
    this.touchJoystick = new FloatingJoystick()
    this.touchControlMode = this.loadTouchControlMode()
    this.directTouch = null
    this.updateTouchControlModeButton()
    
    this.setupEventListeners()
  }
  
  setupEventListeners() {
    document.addEventListener('keydown', (e) => this.onKeyDown(e))
    document.addEventListener('keyup', (e) => this.onKeyUp(e))
    document.addEventListener('wheel', (e) => this.onWheel(e))

    this.touchSurface.addEventListener('pointerdown', (e) => this.onPointerDown(e))
    this.touchSurface.addEventListener('pointermove', (e) => this.onPointerMove(e))
    this.touchSurface.addEventListener('pointerup', (e) => this.onPointerUp(e))
    this.touchSurface.addEventListener('pointercancel', (e) => this.onPointerCancel(e))
    this.touchSurface.addEventListener('lostpointercapture', (e) => this.onPointerCancel(e))
    this.touchSurface.addEventListener('contextmenu', (e) => e.preventDefault())

    this.primaryActionButton?.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'touch') return
      this.touchJoystick.keepAlive(performance.now())
      this.renderTouchJoystick()
    })
    this.primaryActionButton?.addEventListener('click', () => {
      this.activateHudAction()
    })
    this.touchArrowButton?.addEventListener('click', () => this.game.player?.shootArrow())
    this.touchShieldButton?.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'touch') e.preventDefault()
      this.touchShieldHeld = true
    })
    for (const eventName of ['pointerup', 'pointercancel', 'pointerleave']) {
      this.touchShieldButton?.addEventListener(eventName, () => {
        this.touchShieldHeld = false
      })
    }
    this.touchBackButton?.addEventListener('click', () => {
      const player = this.game.player
      if (!player) return
      if (player.isSleeping) this.game.world?.wakeFromHomeSleep?.(false)
      else this.game.world?.standFromHomeCouch?.()
    })
    this.touchControlModeButton?.addEventListener('click', () => {
      this.toggleTouchControlMode()
    })

    window.addEventListener('blur', () => this.resetTransientInput())
    window.addEventListener('resize', () => this.resetTouchJoystick())
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.resetTransientInput()
    })
    
    // Gamepad events
    window.addEventListener('gamepadconnected', (e) => this.onGamepadConnected(e))
    window.addEventListener('gamepaddisconnected', (e) => this.onGamepadDisconnected(e))
  }
  
  onGamepadConnected(e) {
    console.log('Gamepad connected:', e.gamepad.id)
    this.gamepadIndex = e.gamepad.index
  }
  
  onGamepadDisconnected(e) {
    console.log('Gamepad disconnected:', e.gamepad.id)
    if (this.gamepadIndex === e.gamepad.index) {
      this.gamepadIndex = null
      this.gamepadRunActive = false
    }
  }
  
  updateGamepad() {
    const gamepads = navigator.getGamepads()
    if (this.gamepadIndex === null || !gamepads[this.gamepadIndex]) {
      const available = Array.from(gamepads).find(Boolean)
      this.gamepadIndex = available?.index ?? null
    }
    if (this.gamepadIndex === null) return

    const gp = gamepads[this.gamepadIndex]
    if (!gp) return
    
    // Controller mapping used by this game:
    // Axes: 0=physical right stick, 1=physical right stick,
    //       2=physical left stick, 3=physical left stick
    // Buttons: 0=A, 1=B, 2=X, 3=Y, 4=LB, 5=RB, 6=LT, 7=RT, 
    //          8=Back, 9=Start, 10=LStick, 11=RStick, 12=Up, 13=Down, 14=Left, 15=Right
    
    const prevButtons = this.prevGamepadButtons
    const buttons = gp.buttons.map(b => b.pressed)
    const menuDirection = getGamepadMenuDirection(gp)
    const menuNavigation = getGamepadMenuNavigation(gp)
    const minimap = this.game.hud?.minimap

    if (minimap?.expanded) {
      const mapNavigation = getGamepadMapNavigation(gp)
      if (this.gamepadHandActive) this.updateGamepadHand(gp)
      const now = performance.now()
      const verticalChanged = mapNavigation.y !== this.prevGamepadMenuNavigation.y
      const horizontalChanged = mapNavigation.x !== this.prevGamepadMenuNavigation.x
      if (
        mapNavigation.y !== 0 &&
        (verticalChanged || now >= this.gamepadMenuRepeatAt)
      ) {
        minimap.browseArea(mapNavigation.y)
        this.gamepadMenuRepeatAt = now + (verticalChanged ? 320 : 180)
      }
      if (
        mapNavigation.x !== 0 &&
        (horizontalChanged || now >= this.gamepadMenuRepeatAt)
      ) {
        minimap.browseHorizontal(mapNavigation.x)
        this.gamepadMenuRepeatAt = now + (horizontalChanged ? 320 : 180)
      }
      if (buttons[1] && !prevButtons[1]) minimap.setExpanded(false)
      this.prevGamepadButtons = buttons
      this.prevGamepadMenuDirection = menuDirection
      this.prevGamepadMenuNavigation = mapNavigation
      return
    }

    // This controller reports its physical left/right stick clicks in the
    // reverse order from the standard browser mapping.
    if (buttons[11] && !prevButtons[11]) {
      this.setGamepadHandActive(!this.gamepadHandActive)
    }
    this.gamepadRunActive = buttons[10] === true

    if (this.game.isPaused) {
      const now = performance.now()
      const navigationHeld = menuNavigation.x !== 0 || menuNavigation.y !== 0
      const navigationChanged = (
        menuNavigation.x !== this.prevGamepadMenuNavigation.x ||
        menuNavigation.y !== this.prevGamepadMenuNavigation.y
      )
      if (navigationHeld && (navigationChanged || now >= this.gamepadMenuRepeatAt)) {
        this.game.navigateMenuDirection(menuNavigation.x, menuNavigation.y)
        this.gamepadMenuRepeatAt = now + (navigationChanged ? 320 : 180)
      }
      if (buttons[0] && !prevButtons[0]) this.game.activateMenuSelection()
      if (buttons[1] && !prevButtons[1]) this.game.cancelActiveMenu()

      // Start only closes the ordinary pause menu.
      if (
        buttons[9] &&
        !prevButtons[9] &&
        document.getElementById('pause-menu').style.display === 'flex'
      ) {
        this.game.resume()
      }

      if (buttons[8] && !prevButtons[8]) {
        this.toggleDebugMode()
      }

      this.prevGamepadButtons = buttons
      this.prevGamepadMenuDirection = menuDirection
      this.prevGamepadMenuNavigation = menuNavigation
      return
    }

    const player = this.game.player
    if (!player) return

    if (buttons[12] && !prevButtons[12]) {
      this.game.showControlsGuide?.('controller')
      this.prevGamepadButtons = buttons
      return
    }

    if (this.gamepadHandActive) {
      this.updateGamepadHand(gp)
      if (buttons[0] && !prevButtons[0]) this.activateGamepadHandTarget()
      if (buttons[1] && !prevButtons[1]) {
        this.game.hud?.minimap?.setExpanded(false)
      }
      this.prevGamepadButtons = buttons
      this.prevGamepadMenuDirection = menuDirection
      this.prevGamepadMenuNavigation = menuNavigation
      return
    }

    // A button = contextual action (open/interact, otherwise attack)
    if (buttons[0] && !prevButtons[0]) {
      this.activatePrimaryAction()
    }

    // B leaves home furniture immediately.
    if (buttons[1] && !prevButtons[1]) {
      if (player.isSleeping) this.game.world?.wakeFromHomeSleep?.(false)
      else this.game.world?.standFromHomeCouch?.()
    }

    // Y button = fire an arrow
    if (buttons[3] && !prevButtons[3]) {
      player.shootArrow()
    }

    // Start = fruit menu
    if (buttons[9] && !prevButtons[9]) {
      this.game.showFruitMenu()
    }
    
    // Back/Select = pause
    if (buttons[8] && !prevButtons[8]) {
      this.game.pause()
    }
    
    // Store current button state for next frame
    this.prevGamepadButtons = buttons
    this.prevGamepadMenuDirection = menuDirection
    this.prevGamepadMenuNavigation = menuNavigation
  }
  
  getGamepadMovement() {
    if (this.gamepadHandActive) return { x: 0, y: 0 }
    if (this.gamepadIndex === null) return { x: 0, y: 0 }
    
    const gamepads = navigator.getGamepads()
    const gp = gamepads[this.gamepadIndex]
    if (!gp) return { x: 0, y: 0 }

    // R3 changes the player's speed, but movement stays on the right stick.
    return getGamepadMovementVector(gp, this.gamepadDeadzone)
  }

  isRunActive() {
    return this.gamepadRunActive
  }

  setGamepadHandActive(active) {
    this.gamepadHandActive = active === true
    this.gamepadHandLastUpdate = performance.now()
    if (!this.gamepadHand) return

    this.gamepadHand.hidden = !this.gamepadHandActive
    if (this.gamepadHandActive) this.renderGamepadHand()
  }

  drawGamepadHand() {
    const ctx = this.gamepadHand?.getContext?.('2d')
    if (!ctx) return
    const pixels = [
      '...OO...',
      '...OS...',
      '...OS...',
      '...OS...',
      '.OOOS...',
      '.OSSSOO.',
      '..SSSSO.',
      '..SSSSO.',
      '..OSSO..',
      '...OO...'
    ]
    const colors = { O: '#422b24', S: '#fff3d0' }
    ctx.clearRect(0, 0, 8, 10)
    for (let y = 0; y < pixels.length; y++) {
      for (let x = 0; x < pixels[y].length; x++) {
        const color = colors[pixels[y][x]]
        if (!color) continue
        ctx.fillStyle = color
        ctx.fillRect(x, y, 1, 1)
      }
    }
  }

  updateGamepadHand(gamepad) {
    const now = performance.now()
    const delta = Math.min((now - this.gamepadHandLastUpdate) / 1000, 0.05)
    this.gamepadHandLastUpdate = now
    const x = Math.abs(gamepad.axes[2] || 0) >= this.gamepadDeadzone ? gamepad.axes[2] : 0
    const y = Math.abs(gamepad.axes[3] || 0) >= this.gamepadDeadzone ? gamepad.axes[3] : 0
    const speed = 720

    this.gamepadHandPosition.x = Math.max(
      0,
      Math.min(window.innerWidth - 1, this.gamepadHandPosition.x + x * speed * delta)
    )
    this.gamepadHandPosition.y = Math.max(
      0,
      Math.min(window.innerHeight - 1, this.gamepadHandPosition.y + y * speed * delta)
    )
    this.renderGamepadHand()
  }

  renderGamepadHand() {
    if (!this.gamepadHand) return
    const x = Math.round(this.gamepadHandPosition.x / PIXEL_GRID) * PIXEL_GRID
    const y = Math.round(this.gamepadHandPosition.y / PIXEL_GRID) * PIXEL_GRID
    this.gamepadHand.style.transform = `translate3d(${x}px, ${y}px, 0)`
  }

  activateGamepadHandTarget() {
    const target = document.elementFromPoint(
      this.gamepadHandPosition.x,
      this.gamepadHandPosition.y
    )
    const minimapButton = target?.closest?.('#minimap-button')
    if (!minimapButton || minimapButton.hidden) return false
    minimapButton.click()
    return true
  }
  
  onKeyDown(e) {
    if (!this.keys[e.code]) {
      this.keysJustPressed[e.code] = true
    }
    this.keys[e.code] = true

    if (this.game.hud?.minimap?.expanded) {
      if (e.code === 'KeyW' || e.code === 'ArrowUp') {
        e.preventDefault()
        this.game.hud.minimap.browseArea(-1)
        return
      }
      if (e.code === 'KeyS' || e.code === 'ArrowDown') {
        e.preventDefault()
        this.game.hud.minimap.browseArea(1)
        return
      }
      if (e.code === 'KeyA' || e.code === 'ArrowLeft') {
        e.preventDefault()
        this.game.hud.minimap.browseHorizontal(-1)
        return
      }
      if (e.code === 'KeyD' || e.code === 'ArrowRight') {
        e.preventDefault()
        this.game.hud.minimap.browseHorizontal(1)
        return
      }
      if (e.code === 'Escape' || e.code === 'KeyB') {
        e.preventDefault()
        this.game.hud.minimap.setExpanded(false)
        return
      }
    }
    
    if (
      (e.code === 'Escape' || e.code === 'KeyB') &&
      this.game.isRunning &&
      this.game.isPaused
    ) {
      e.preventDefault()
      this.game.cancelActiveMenu()
      return
    }

    if (e.code === 'Escape' && this.game.isRunning) {
      e.preventDefault()
      this.game.pause()
      return
    }

    if (this.game.isPaused) {
      if (e.code === 'KeyW' || e.code === 'ArrowUp') {
        e.preventDefault()
        this.game.navigateMenu(-1)
        return
      }
      if (e.code === 'KeyS' || e.code === 'ArrowDown') {
        e.preventDefault()
        this.game.navigateMenu(1)
        return
      }
      if (e.code === 'Space' || e.code === 'Enter' || e.code === 'KeyE') {
        e.preventDefault()
        this.game.activateMenuSelection()
        return
      }
    }
    
    // Spacebar uses the context action: interact nearby, otherwise attack.
    if (e.code === 'Space' && this.game.isRunning && !this.game.isPaused && this.game.player) {
      e.preventDefault()
      this.activatePrimaryAction()
    }

    if (e.code === 'KeyE' && this.game.isRunning && !this.game.isPaused) {
      e.preventDefault()
      this.activateInteractAction()
    }

    if (e.code === 'KeyB' && this.game.player && this.game.isRunning && !this.game.isPaused) {
      e.preventDefault()
      if (this.game.player.isSleeping) this.game.world?.wakeFromHomeSleep?.(false)
      else if (!this.game.world?.standFromHomeCouch?.()) {
        this.game.world?.pickUpHomeReturnMarker?.()
      }
    }

    if (e.code === 'KeyY' && this.game.isRunning && !this.game.isPaused) {
      e.preventDefault()
      this.game.player?.shootArrow()
    }

    
    // Dev mode toggles
    if (e.code === 'KeyG' && e.ctrlKey && this.game.player) {
      this.game.player.godMode = !this.game.player.godMode
      console.log('God mode:', this.game.player.godMode ? 'ON' : 'OFF')
    }
    
    if (e.code === 'KeyR' && e.ctrlKey && this.game.player) {
      this.game.player.hearts = this.game.player.maxHearts
      console.log('Hearts restored')
    }
    
    // Debug zoom - show full map width
    if (e.code === 'KeyM' && e.ctrlKey) {
      e.preventDefault()
      this.toggleDebugMode()
    }
  }
  
  onKeyUp(e) {
    this.keys[e.code] = false
  }

  onPointerDown(e) {
    this.mouseX = e.clientX
    this.mouseY = e.clientY
    if (!this.game.isRunning || this.game.isPaused) return

    if (e.pointerType === 'touch') {
      e.preventDefault()
      if (this.touchControlMode === 'direct') {
        this.beginDirectTouch(e.pointerId, e.clientX, e.clientY, performance.now())
        try {
          this.touchSurface.setPointerCapture(e.pointerId)
        } catch {
          // Pointer capture may already have been released by the browser.
        }
        return
      }
      const result = this.touchJoystick.begin(
        e.pointerId,
        e.clientX,
        e.clientY,
        performance.now()
      )

      if (result.handled) {
        try {
          this.touchSurface.setPointerCapture(e.pointerId)
        } catch {
          // Pointer capture may already have been released by the browser.
        }
      }
      this.renderTouchJoystick()
      return
    }

    if (e.button === 0 && e.isPrimary !== false) {
      this.mouseDown.left = true
      this.activatePrimaryAction()
    }
  }

  onPointerMove(e) {
    this.mouseX = e.clientX
    this.mouseY = e.clientY
    if (e.pointerType !== 'touch') return

    e.preventDefault()
    if (this.touchControlMode === 'direct') {
      this.moveDirectTouch(e.pointerId, e.clientX, e.clientY, performance.now())
      return
    }
    this.touchJoystick.move(
      e.pointerId,
      e.clientX,
      e.clientY,
      performance.now()
    )
    this.renderTouchJoystick()
  }

  onPointerUp(e) {
    if (e.pointerType === 'touch') {
      e.preventDefault()
      const now = performance.now()
      if (this.touchControlMode === 'direct') {
        const result = this.endDirectTouch(
          e.pointerId,
          e.clientX,
          e.clientY,
          now
        )
        if (result.tap) this.activateTouchPoint(e.clientX, e.clientY)
        if (this.touchSurface.hasPointerCapture?.(e.pointerId)) {
          this.touchSurface.releasePointerCapture(e.pointerId)
        }
        return
      }
      const result = this.touchJoystick.end(
        e.pointerId,
        e.clientX,
        e.clientY,
        now
      )
      this.renderTouchJoystick()

      if (result.tap) {
        this.activateTouchPoint(e.clientX, e.clientY)
      }

      if (this.touchSurface.hasPointerCapture?.(e.pointerId)) {
        this.touchSurface.releasePointerCapture(e.pointerId)
      }
      return
    }

    if (e.button === 0 && e.isPrimary !== false) {
      this.mouseDown.left = false
    }
  }

  onPointerCancel(e) {
    if (e.pointerType !== 'touch') return
    if (this.touchControlMode === 'direct') {
      this.cancelDirectTouch(e.pointerId)
      return
    }
    this.touchJoystick.cancel(e.pointerId, performance.now())
    this.renderTouchJoystick()
  }

  activatePrimaryAction() {
    if (!this.game.isRunning || this.game.isPaused || !this.game.player) {
      return false
    }

    if (this.game.world?.interactWithHome?.(this.game.player.interactRange)) {
      return true
    }

    if (this.game.world?.interactWithVillageGate?.(this.game.player.interactRange)) {
      return true
    }

    if (this.game.world?.interactWithVillageHouse?.(this.game.player.interactRange)) {
      return true
    }

    if (this.game.itemManager?.interactWithNearestChest(
      this.game.player.interactRange,
      false
    )) {
      return true
    }

    this.game.player.attack()
    return true
  }

  activateHudAction() {
    if (this.activateInteractAction()) return true
    return this.activatePrimaryAction()
  }

  activateInteractAction() {
    if (!this.game.isRunning || this.game.isPaused || !this.game.player) {
      return false
    }

    if (this.game.world?.interactWithHome?.(this.game.player.interactRange)) {
      return true
    }

    if (this.game.world?.interactWithVillageGate?.(this.game.player.interactRange)) {
      return true
    }

    if (this.game.world?.interactWithVillageHouse?.(this.game.player.interactRange)) {
      return true
    }

    return this.game.itemManager?.interactWithNearestChest(
      this.game.player.interactRange,
      true
    ) === true
  }

  getMovementVector() {
    if (this.touchControlMode === 'direct' && this.directTouch) {
      this.updateDirectTouch(performance.now())
      if (this.directTouch.active) {
        return clampMovementVector(this.directTouch.vector)
      }
    }

    const touch = this.touchJoystick.snapshot()
    if (touch.active) {
      return clampMovementVector(touch.vector)
    }

    const keyboard = {
      x: (this.keys.KeyD === true ? 1 : 0) - (this.keys.KeyA === true ? 1 : 0),
      y: (this.keys.KeyS === true ? 1 : 0) - (this.keys.KeyW === true ? 1 : 0)
    }
    if (keyboard.x !== 0 || keyboard.y !== 0) {
      return clampMovementVector(keyboard)
    }

    return clampMovementVector(this.getGamepadMovement())
  }

  isShieldHeld() {
    if (this.keys.KeyX === true || this.touchShieldHeld) return true
    if (this.gamepadIndex === null || !navigator.getGamepads) return false
    const gamepad = navigator.getGamepads()[this.gamepadIndex]
    return gamepad?.buttons?.[2]?.pressed === true
  }

  resetTouchJoystick() {
    this.touchJoystick.reset()
    this.directTouch = null
    this.touchShieldHeld = false
    this.renderTouchJoystick()
  }

  resetTransientInput() {
    this.keys = {}
    this.keysJustPressed = {}
    this.mouseDown.left = false
    this.resetTouchJoystick()
  }

  loadTouchControlMode() {
    try {
      return localStorage.getItem('poesTouchControlMode') === 'direct'
        ? 'direct'
        : 'joystick'
    } catch {
      return 'joystick'
    }
  }

  toggleTouchControlMode() {
    this.touchControlMode = this.touchControlMode === 'joystick' ? 'direct' : 'joystick'
    this.resetTouchJoystick()
    try {
      localStorage.setItem('poesTouchControlMode', this.touchControlMode)
    } catch {
      // Storage may be unavailable in private browsing.
    }
    this.updateTouchControlModeButton()
    return this.touchControlMode
  }

  updateTouchControlModeButton() {
    if (!this.touchControlModeButton) return
    const label = this.touchControlMode === 'direct'
      ? 'Touch: Direct'
      : 'Touch: Joystick'
    this.touchControlModeButton.textContent = label
    this.touchControlModeButton.setAttribute(
      'aria-label',
      `${label}. Activate to switch touch control style.`
    )
  }

  getTouchDirection(x, y) {
    const rect = this.touchSurface.getBoundingClientRect?.() || {
      left: 0,
      top: 0,
      width: window.innerWidth,
      height: window.innerHeight
    }
    const dx = x - (rect.left + rect.width / 2)
    const dy = y - (rect.top + rect.height / 2)
    const distance = Math.hypot(dx, dy)
    return distance > 0
      ? { x: dx / distance, y: dy / distance }
      : { x: 0, y: 0 }
  }

  beginDirectTouch(pointerId, x, y, now) {
    if (this.directTouch || pointerId == null) return false
    this.directTouch = {
      pointerId,
      x,
      y,
      startedAt: now,
      active: false,
      vector: { x: 0, y: 0 }
    }
    return true
  }

  moveDirectTouch(pointerId, x, y, now) {
    if (!this.directTouch || this.directTouch.pointerId !== pointerId) return false
    this.directTouch.x = x
    this.directTouch.y = y
    this.updateDirectTouch(now)
    return true
  }

  updateDirectTouch(now) {
    if (!this.directTouch) return false
    if (now - this.directTouch.startedAt < DIRECT_TOUCH_HOLD_MS) return false
    this.directTouch.active = true
    this.directTouch.vector = this.getTouchDirection(
      this.directTouch.x,
      this.directTouch.y
    )
    return true
  }

  endDirectTouch(pointerId, x, y, now) {
    if (!this.directTouch || this.directTouch.pointerId !== pointerId) {
      return { handled: false, tap: false }
    }
    const duration = now - this.directTouch.startedAt
    this.directTouch = null
    return {
      handled: true,
      tap: duration < DIRECT_TOUCH_HOLD_MS
    }
  }

  cancelDirectTouch(pointerId) {
    if (!this.directTouch || this.directTouch.pointerId !== pointerId) return false
    this.directTouch = null
    return true
  }

  attackTowardScreenPoint(x, y) {
    const player = this.game.player
    if (!player) return false
    const direction = this.getTouchDirection(x, y)
    if (Math.abs(direction.x) > Math.abs(direction.y)) {
      player.facing = direction.x < 0 ? 'left' : 'right'
    } else if (direction.y !== 0) {
      player.facing = direction.y < 0 ? 'up' : 'down'
    }
    player.attack()
    return true
  }

  activateTouchPoint(x, y) {
    const worldPoint = this.game.renderer?.screenToWorld?.(x, y)
    if (worldPoint) {
      const range = this.game.player?.interactRange
      if (this.game.world?.interactWithHomeDoorAt?.(
        worldPoint.x,
        worldPoint.y,
        range
      )) return true
      if (this.game.world?.interactWithVillageGateAt?.(
        worldPoint.x,
        worldPoint.y,
        range
      )) return true
      if (this.game.world?.interactWithVillageHouseDoorAt?.(
        worldPoint.x,
        worldPoint.y,
        range
      )) return true
    }
    if (this.touchControlMode === 'direct') {
      return this.attackTowardScreenPoint(x, y)
    }
    return this.activatePrimaryAction()
  }

  renderTouchJoystick() {
    if (!this.touchJoystickElement || !this.touchJoystickKnob) return

    const state = this.touchJoystick.snapshot()
    this.touchJoystickElement.classList.toggle('is-visible', state.visible)
    this.touchJoystickElement.classList.toggle('is-active', state.active)

    if (!state.anchor || !state.knob) return

    const baseX = Math.round((state.anchor.x - JOYSTICK_SIZE / 2) / PIXEL_GRID) * PIXEL_GRID
    const baseY = Math.round((state.anchor.y - JOYSTICK_SIZE / 2) / PIXEL_GRID) * PIXEL_GRID
    const knobX = Math.round((state.knob.x - state.anchor.x) / PIXEL_GRID) * PIXEL_GRID
    const knobY = Math.round((state.knob.y - state.anchor.y) / PIXEL_GRID) * PIXEL_GRID

    this.touchJoystickElement.style.transform = `translate3d(${baseX}px, ${baseY}px, 0)`
    this.touchJoystickKnob.style.transform = `translate3d(${knobX}px, ${knobY}px, 0)`
  }

  onWheel(e) {
    return
  }

  toggleDebugMode() {
    const r = this.game.renderer
    r.debugZoom = !r.debugZoom
    if (r.debugZoom) {
      // Fit the widest zone (120 tiles) across the screen width
      r.zoom = r.canvas.width / (130 * r.tileSize)
    } else {
      r.zoom = r.defaultZoom
    }
    console.log('Debug zoom:', r.debugZoom ? 'ON' : 'OFF')
  }
  
  isKeyPressed(code) {
    // Check keyboard
    if (this.keys[code] === true) return true
    
    // Check gamepad equivalents
    const gp = this.getGamepadMovement()
    
    switch (code) {
      case 'KeyW': return gp.y < -0.1
      case 'KeyS': return gp.y > 0.1
      case 'KeyA': return gp.x < -0.1
      case 'KeyD': return gp.x > 0.1
    }
    
    return false
  }
  
  justPressed(code) {
    return this.keysJustPressed[code] === true
  }
  
  // Call this at end of each frame to clear just-pressed state
  clearJustPressed() {
    this.keysJustPressed = {}
  }
  
  // Call once per frame to poll gamepad
  update() {
    this.updateGamepad()

    if (!this.game.isRunning || this.game.isPaused) {
      const touch = this.touchJoystick.snapshot()
      if (touch.visible || touch.active || touch.pending) this.resetTouchJoystick()
      return
    }

    if (this.touchControlMode === 'direct') {
      this.updateDirectTouch(performance.now())
    }
    if (this.touchJoystick.advance(performance.now())) {
      this.renderTouchJoystick()
    }
  }
  
  // No longer needed for 2D, but keep for compatibility
  lock() {}
  unlock() {}
}
